import { useQuery, useMutation } from "@tanstack/react-query"
import type { QueryFunctionContext } from "@tanstack/react-query"

import dayjs from "@/helpers/dayjs"
import { formatTagDictQuery, formatTagDictMutation } from "@/pages/helpers"
import { getIdFromUri } from "@/helpers"
import { sanitizeUserCommentTextHtml } from "shared/forms/helperFunctions"

import { getCommentTypeChoices } from "shared/forms/LogInteraction/selectors"

import type { FormValues } from "@/pages/log-comment/types"
import type { CustomField, Interaction } from "@/types/djangio"

// queryKey factory
const commentKeys = {
    all: [{ _model: "comments" }] as const,
    detail: (datumId: string) => [{ ...commentKeys.all[0], datumId }] as const,
}

const fetchComment = async ({
    queryKey,
}: QueryFunctionContext<ReturnType<(typeof commentKeys)["detail"]>>): Promise<Interaction> => {
    // Interaction Interface being used as it encapsulates all attr for Notes, including comment specific attributes
    const [{ datumId }] = queryKey

    const response = await DjangIO.app.userdata.notes.models.Note.objects.get(datumId).GET()

    return response
}
// Hook to be exported
export const useComment = (datumId: string) =>
    useQuery({
        queryKey: commentKeys.detail(datumId),
        queryFn: fetchComment,
        enabled: Boolean(datumId),
        select: (data) => {
            // commentItems
            const type_object: Readonly<{ note_through_field: string }> = DjangIO.app.models.QuorumDataType.by_value(
                data.comment_type,
            ) as any

            const commentItemsIds: number[] = data[type_object.note_through_field].map((itemUri: string) =>
                parseInt(getIdFromUri(itemUri), 10),
            )

            // Django's TIME_ZONE is set to America/New_York, so we'll use that to parse the date, but then convert to the user's timezone
            const date = dayjs.tz(data.date, "America/New_York").tz(dayjs.tz.guess())
            const time = date.format("HH:mm")

            const issueIds: number[] = data.projects.map((projectUri: string) =>
                DjangIO.app.userdata.models.Project.idFromResourceUri(projectUri),
            )

            // Date CustomFields are returned in the format YYYY-MM-DD,
            // but the DatePickerInput component expects a Date object. We'll transform here to maintain backwards compat
            const tagDict = formatTagDictQuery(data.tag_dict)

            const limitedToTeams: number[] = data.limited_to_teams.map((teamUri: string) =>
                DjangIO.app.userdata.models.Team.idFromResourceUri(teamUri),
            )

            const isCommentTypeIssue = data.comment_type === DjangIO.app.models.QuorumDataType.issue_management.value

            const commentType =
                // Parse Comment objects with Comment Types of
                // DjangIO.app.models.QuorumDataType.project.value
                // DjangIO.app.models.QuorumDataType.issue_management.value
                data.comment_type === DjangIO.app.models.QuorumDataType.project.value
                    ? DjangIO.app.models.QuorumDataType.issue_management.value
                    : data.comment_type

            return {
                type: commentType,
                commentItems: commentItemsIds,
                date: date.toDate(),
                time,
                noteText: sanitizeUserCommentTextHtml(data.text),
                issues: isCommentTypeIssue ? commentItemsIds : issueIds,
                attachments: data?.cache_json?.attachments,
                limitedToTeams,
                isVisibleToPartners: data.is_visible_to_partners,
                tagDict,
                teamPermissioning: data.limited_to_teams.length ? "limited_to_teams" : "everyone",
                userId: data.user_id,
            }
        },
    })

export const useMutateComment = (customFields: CustomField[], datumId?: string) =>
    useMutation({
        mutationFn: (values: FormValues) => {
            // {Basic Info - Date Type}
            const [hours, minutes] = values.time.split(":")
            const date = dayjs(values.date).hour(parseInt(hours, 10)).minute(parseInt(minutes, 10))

            const getLimitedToTeams = () => {
                if (values.teamPermissioning === "everyone") {
                    return []
                }
                return values.limitedToTeams.map((team) =>
                    typeof team === "number"
                        ? DjangIO.app.userdata.models.Team.resourceUriFromId(team)
                        : team.resource_uri,
                )
            }

            // Use the browser's default locale
            const searchable_date = date.format("MMMM D, YYYY [at] h:mm A z")

            // Find QuorumDataType by Value
            const quorumDataType: { note_through_field: string } & ReturnType<
                typeof DjangIO.app.models.QuorumDataType.by_value
            > = DjangIO.app.models.QuorumDataType.by_value(values.type) as any
            const qdtModel = DjangIO.resourceFromDataType(quorumDataType.value)
            const typeChoices: { note_through_field: string }[] & ReturnType<typeof getCommentTypeChoices> =
                getCommentTypeChoices() as any

            const isCommentTypeIssue = values.type === DjangIO.app.models.QuorumDataType.issue_management.value

            const parsedIssues = () => {
                if (isCommentTypeIssue) {
                    const commentItemsURIs = values.commentItems.map((item: number) =>
                        DjangIO.app.userdata.models.Project.resourceUriFromId(item),
                    )
                    return commentItemsURIs
                } else {
                    // to handle separate Issues select items when not Comment Type of Issue
                    if (values.issues) {
                        const issuesURIs = values.issues.map((issue) =>
                            typeof issue === "number"
                                ? DjangIO.app.userdata.models.Project.resourceUriFromId(issue)
                                : issue.resource_uri,
                        )
                        return issuesURIs
                    } else {
                        return []
                    }
                }
            }

            const tagDict = formatTagDictMutation(values.tagDict, customFields)

            // When creating a new Comment, if characters like < > are entered by a user,
            // the noteText string contains those characters as HTML entities that
            // need to be decoded (ex. from &lt; to <) before getting sanitized of
            // non-whitelisted HTML with sanitizeUserCommentTextHtml, before getting sent to
            // the server.
            const decodeHtml = (text) => {
                const doc = new DOMParser().parseFromString(text, "text/html")
                const decodedInnerHtml = doc.documentElement.innerHTML.replace(/&lt;/g, "<").replace(/&gt;/g, ">")
                return decodedInnerHtml
            }
            const decodedNoteText = decodeHtml(values.noteText)
            const sanitizedNoteText = sanitizeUserCommentTextHtml(decodedNoteText)

            const data: Partial<Interaction> = {
                comment_type: isCommentTypeIssue ? DjangIO.app.models.QuorumDataType.project.value : values.type,
                // Send UTC to the server
                date: date.toISOString(),
                // And a searchable_date in the user's timezone
                searchable_date,
                // TODO: What is this?
                privacy: 2,

                // {Notes} Note
                text: sanitizedNoteText,

                // {Notes} Attachments
                attachments: values.attachments.map((attachment) =>
                    DjangIO.app.userdata.models.Attachment.resourceUriFromId(attachment.id),
                ),

                // {Basic Info} commentItems
                ...{
                    // unset relations
                    ...typeChoices.reduce(
                        (relations, type) => ({
                            ...relations,
                            [type.note_through_field]: [],
                        }),
                        {},
                    ),
                    // then set it for the chosen type
                    [quorumDataType.note_through_field]: values.commentItems.map((itemId) =>
                        qdtModel.foreignKey(itemId),
                    ),
                },

                projects: parsedIssues(),

                // Custom Fields
                tag_dict: tagDict,

                // Team Permissioning
                is_visible_to_partners: values.isVisibleToPartners,
                limited_to_teams: getLimitedToTeams(),
            }

            // If we have a datumId, we're updating an existing comment
            if (datumId) {
                return DjangIO.app.userdata.notes.models.Note.objects.get(datumId).update(data).PATCH()
            }

            // We're creating a new comment
            return DjangIO.app.userdata.notes.models.Note.objects
                .create({
                    ...data,
                    // Only set the user and type if this is a new comment
                    user: DjangIO.app.userdata.models.QuorumUser.resourceUriFromId(Userdata.id),
                    note_type: DjangIO.app.userdata.types.NoteType.comment.value,
                })
                .POST()
        },
    })
